import {
	configurators, products, extras, upsells, testimonials, orders,
} from '@/agent';
import snipcart, { items } from '@/utils/snipcart';

const actions = {
	// =========================================================================
	// DATA FETCHING
	// =========================================================================

	async fetchConfiguratorData({ commit }, { configuratorUID }) {
		const configuratorData = await configurators.get(configuratorUID);
		commit('setConfigurator', { configuratorData });
	},

	async fetchProducts({ commit }, { configuratorId }) {
		const filteredProducts = await products.findByConfiguratorId(configuratorId);
		commit('setProducts', { products: filteredProducts });
	},

	async fetchExtras({ commit }, { productId }) {
		const filteredExtras = await extras.findByProductId(productId);
		commit('setExtras', { extras: filteredExtras });
	},

	async fetchUpsells({ commit }, { productId }) {
		const filteredUpsells = await upsells.findByProductId(productId);
		commit('setUpsells', { upsells: filteredUpsells });
	},

	async fetchTestimonials({ commit }) {
		const allTestimonials = await testimonials.all();
		commit('setTestimonials', { testimonials: allTestimonials });
	},

	// =========================================================================
	// ORDER STATE
	// =========================================================================

	setSelectedProduct({ commit }, { productId }) {
		commit('addProductToOrder', { productId });
	},

	setSelectedExtras({ commit }, { selectedExtras }) {
		commit('addExtrasToOrder', { selectedExtras });
	},

	removeSelectedExtras({ commit }) {
		commit('removeAllExtrasFromOrder');
	},

	setSelectedUpsells({ commit }, { selectedUpsells }) {
		commit('addUpsellsToOrder', { selectedUpsells });
	},

	removeSelectedUpsells({ commit }) {
		commit('removeAllUpsellsFromOrder');
	},

	setOrderDetails({ commit }, { formData }) {
		commit('addDetailsToOrder', { formData });
	},

	// =========================================================================
	// SNIPCART — Build product definition & add to cart
	// =========================================================================

	/**
	 * Builds a Snipcart product definition from the current order state
	 * without adding it to the cart. Used by the iframe checkout redirect
	 * flow to pass the definition to the new tab via localStorage.
	 */
	buildSnipcartProductDefinition({ state }, { configuratorId, orderValue, description }) {
		const { product: selectedProduct, extras: selectedExtras, upsells: selectedUpsells, details } = state.order;
		const product = state.products.find((p) => p.id === selectedProduct);
		const productTitle = product ? product.Title : '';

		// Build validation URL so productData.php can verify the total price
		const validationUrl = new URL('https://citygames-bremen.de/konfigurator/productData.php');
		validationUrl.searchParams.set('id', selectedProduct);
		validationUrl.searchParams.set('attendees', details.peopleCount);
		if (selectedExtras.length > 0) {
			validationUrl.searchParams.set('extras', selectedExtras.join(','));
		}
		if (selectedUpsells.length > 0) {
			validationUrl.searchParams.set('upsells', selectedUpsells.join(','));
		}

		const uniqueId = `configurator-${selectedProduct}-${Date.now()}`;
		validationUrl.searchParams.set('uid', uniqueId);

		return snipcart.createProductDefinition({
			id: uniqueId,
			name: `CityGames - ${productTitle}`,
			description,
			price: orderValue,
			url: validationUrl.toString(),
			quantity: 1,
			metadata: {
				tld: window.location.hostname,
				configurator: configuratorId,
				businessCustomer: details.businessCustomer,
				FirstName: details.firstName,
				LastName: details.lastName,
				EmailAdress: details.emailAdress,
				PhoneNumber: details.phoneNumber,
				Company: details.company,
				Street: details.street,
				ZipCode: details.zipCode,
				City: details.city,
				PersonalMessage: details.personalMessage,
				AgbConfirmation: details.agbConfirmation,
				DSGVO: details.dsgvo,
				PeopleCount: details.peopleCount,
				PlayDateTime: `${details.playDate} ${details.playTime}`,
				OrderValue: orderValue,
				product: selectedProduct,
				extras: selectedExtras,
				upsells: selectedUpsells,
			},
		});
	},

	async addToSnipcart({ dispatch }, { configuratorId, orderValue, description }) {
		const productDefinition = await dispatch('buildSnipcartProductDefinition', { configuratorId, orderValue, description });
		await items.add(productDefinition);
	},

	// =========================================================================
	// REQUEST — Submit inquiry via API
	// =========================================================================

	async submitRequest({ state }, { configuratorId, orderValue }) {
		const { product: selectedProduct, extras: selectedExtras, upsells: selectedUpsells, details } = state.order;

		await orders.post({
			configurator: [configuratorId],
			businessCustomer: details.businessCustomer,
			FirstName: details.firstName,
			LastName: details.lastName,
			EmailAdress: details.emailAdress,
			PhoneNumber: details.phoneNumber,
			Company: details.company,
			Street: details.street,
			ZipCode: details.zipCode,
			City: details.city,
			PersonalMessage: details.personalMessage,
			AgbConfirmation: details.agbConfirmation,
			DSGVO: details.dsgvo,
			Request: true,
			PeopleCount: details.peopleCount,
			PlayDateTime: `${details.playDate} ${details.playTime}`,
			OrderValue: orderValue,
			product: selectedProduct,
			extras: selectedExtras,
			upsells: selectedUpsells,
		});
	},
};

export { actions as default };
